import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import useHandleScroll from "hooks/useHandleScroll";
import {
  IShippingAddressCreate,
  IShippingAddressForm,
  IShippingAddressUpdate,
  IShippingDest,
} from "features/mst_shipping_dest/types/client_detail";
import NoteOutlinedIcon from "@mui/icons-material/NoteOutlined";
import prefecture from "util/prefecture";
import Notify from "config/notify";
import handleError from "util/handle_error";
import ShippingDetailModal from "../ShippingDetailModal";
import { TableButtonSxProps, TableContainerSXProps, TableRowSxProps } from "./styles";

interface IShippingAddressTableProps {
  addressList: IShippingDest[];
  hasMore: number;
  setPage: () => void;
  onCreate: (data: IShippingAddressCreate) => Promise<any>;
  onUpdate: (data: IShippingAddressUpdate) => Promise<any>;
  isFetching: boolean;
}

const defaultShippingDest: IShippingAddressForm = {
  client_cd: "",
  client_name: "",
  delete_flag: 0,
  id: 0,
  address: "",
  pref_cd: "",
  postal_cd: "",
  shipping_dest_name: "",
  shipping_dest_tel: "",
  fax_no: "",
};

/**
 * ShippingAddressTable component renders a table displaying a list of shipping addresses.
 * It includes functionality for pagination, modal display for new address registration,
 * and dynamic table content based on the provided address list.
 * @param root0
 * @example
 * return (
 *   <ShippingAddressTable />
 * )
 * @returns {Element} A component that displays a table of shipping addresses.
 * @remarks
 * - The component uses `useState` to manage the modal visibility and current page.
 * - The `useShippingAddressList` hook is used to fetch the list of addresses and determine if there are more addresses to load.
 * - The `useHandleScroll` hook is used to handle the scroll event for pagination.
 * - The table columns are defined in the `TableColumns` array.
 * - The component includes a button to open a modal for new address registration.
 * - The table displays the address list with alternating row colors for better readability.
 * - The modal for address details is conditionally rendered based on the `isShowModal` state.
 */
function ShippingAddressTable({
  addressList,
  hasMore,
  setPage,
  onCreate,
  onUpdate,
  isFetching,
}: IShippingAddressTableProps) {
  const [isShowModal, setIsShowModal] = useState(false);
  const { handleScroll } = useHandleScroll(!!hasMore, setPage);
  const [selectedShippingDest, setSelectedShippingDest] = useState<IShippingDest>();
  const [shippingDestForm, setShippingDestForm] =
    useState<IShippingAddressForm>(defaultShippingDest);

  const clientInfo = {
    client_cd: addressList[0]?.client_cd,
    client_name: addressList[0]?.mst_client.client_name,
    pref_cd: addressList[0]?.pref_cd,
    address: addressList[0]?.address,
    shipping_dest_name: addressList[0]?.shipping_dest_name,
    id: addressList[0]?.id,
  };

  useEffect(() => {
    if (isShowModal && selectedShippingDest) {
      setShippingDestForm(() => ({
        client_cd: clientInfo.client_cd,
        client_name: clientInfo.client_name,
        id: selectedShippingDest.id,
        delete_flag: 0,
        address: selectedShippingDest.address ?? "",
        pref_cd: selectedShippingDest.pref_cd ?? "",
        postal_cd: selectedShippingDest.postal_cd ?? "",
        shipping_dest_name: selectedShippingDest.shipping_dest_name ?? "",
        shipping_dest_tel: selectedShippingDest.shipping_dest_tel ?? "",
        fax_no: selectedShippingDest.mst_yh_store_client.fax_no ?? "",
      }));
      return;
    }
    if (isShowModal && !selectedShippingDest) {
      setShippingDestForm(() => ({
        ...defaultShippingDest,
        client_cd: clientInfo.client_cd,
        client_name: clientInfo.client_name,
        id: clientInfo.id ?? "",
        delete_flag: 0,
      }));
      return;
    }
    setShippingDestForm(defaultShippingDest);
  }, [isShowModal, selectedShippingDest]);

  const TableColumns = ["発送先ID", "発送先名", "住所", "詳細"];
  const handleSubmit = async (isDeleted: boolean = false) => {
    try {
      if (selectedShippingDest) {
        await onUpdate({
          id: selectedShippingDest.id,
          delete_flag: isDeleted ? 1 : shippingDestForm.delete_flag,
          address: shippingDestForm.address,
          pref_cd: shippingDestForm.pref_cd,
          postal_cd: shippingDestForm.postal_cd,
          shipping_dest_name: shippingDestForm.shipping_dest_name,
          shipping_dest_tel: shippingDestForm.shipping_dest_tel,
        });
      } else {
        await onCreate({
          client_cd: shippingDestForm.client_cd,
          delete_flag: shippingDestForm.delete_flag,
          address: shippingDestForm.address,
          pref_cd: shippingDestForm.pref_cd,
          postal_cd: shippingDestForm.postal_cd,
          shipping_dest_name: shippingDestForm.shipping_dest_name,
          shipping_dest_tel: shippingDestForm.shipping_dest_tel,
        });
      }
      if (selectedShippingDest) {
        Notify.setNotification({
          show: true,
          title: isDeleted ? "削除しました" : "更新が完了しました",
          confirmLabel: "OK",
          actionsPosition: "center",
        });
      } else {
        Notify.setNotification({
          show: true,
          title: "新規登録しました",
          confirmLabel: "OK",
          actionsPosition: "end",
        });
      }
    } catch (e: any) {
      if (e.response.data.message) {
        Notify?.setNotification({
          show: true,
          message: e.response.data.message,
        });
      } else {
        Notify?.setNotification({
          show: true,
          message: handleError(e),
        });
      }
    } finally {
      setSelectedShippingDest(undefined);
      setIsShowModal(false);
      setShippingDestForm(defaultShippingDest);
    }
  };

  return (
    <Box
      p={{ xs: 2, md: 2.5, lg: 5 } }
      display="flex"
      flexDirection="column"
      alignItems="center"
      bgcolor="var(--offwhite-100)"
      height="100%"
    >
      <Box
        sx={{ width: { xs: "100%", lg: 1254}}}
        display="flex"
        justifyContent="space-between"
        alignItems="center"
      >
        <Button
          disabled={isFetching}
          sx={{ ...TableButtonSxProps, color: "var(--gray-700)" }}
          variant="outlined"
          startIcon={<NoteOutlinedIcon sx={{ width: "22px", height: "23px" }} />}
          onClick={() => {
            setIsShowModal(true);
            setSelectedShippingDest(undefined);
          }}
        >
          新規登録
        </Button>
        <Typography fontWeight="regular" mr={{xs: 0, md: 5}} color="black" fontSize="20px">
          件数【{addressList.length}件】
        </Typography>
      </Box>
      <Box sx={{ width: "100%", overflowX: "auto" }}>
        <TableContainer sx={TableContainerSXProps} onScroll={handleScroll}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                {TableColumns.map((column) => (
                  <TableCell align="center" sx={TableRowSxProps} key={column}>
                    {column}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {addressList.map((address: IShippingDest, index) => (
                <TableRow
                  key={address.id}
                  sx={index % 2 ? { bgcolor: "var(--offwhite-300)" } : { bgcolor: "var(--white)" }}
                >
                  <TableCell align="center" sx={{ width: "204px", fontSize: { xs: 16, md: 18, lg: 20 } }}>
                    {address.id}
                  </TableCell>
                  <TableCell align="left" sx={{ fontSize: { xs: 16, md: 18, lg: 20 }, width: "414px" }}>
                    {address.shipping_dest_name}
                  </TableCell>
                  <TableCell sx={{ fontSize: { xs: 16, md: 18, lg: 20 }, width: "414px" }}>
                    {`${prefecture.find((v) => v.code === address.pref_cd)?.name ?? ""} ${address.address}`}
                  </TableCell>
                  <TableCell align="center" sx={{ width: "204px" }}>
                    <Button
                      sx={{ ...TableButtonSxProps, color: "var(--black)" }}
                      variant="outlined"
                      onClick={() => {
                        setIsShowModal(true);
                        setSelectedShippingDest(address);
                      }}
                    >
                      詳細
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
      {isShowModal && (
        <ShippingDetailModal
          onClose={() => setIsShowModal(false)}
          isOpen={isShowModal}
          shippingDest={selectedShippingDest}
          shippingDestForm={shippingDestForm}
          onSubmit={handleSubmit}
          onChange={(data: IShippingAddressForm) => {
            setShippingDestForm((prev) => ({ ...prev, ...data }));
          }}
          isFetching={isFetching}
        />
      )}
    </Box>
  );
}

export default ShippingAddressTable;
